import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Tooltip,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogContent,
  Fab,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem as MenuListItem
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import MenuIcon from "@mui/icons-material/Menu";
import localApi from "../externalApi/local.js";

// Таблица цен для разных сроков и количества сотрудников
// Обновлено: добавлен тариф trial с нулевой стоимостью для всех вариантов
const priceTable = {
  month: { "25": 1500, "50": 2000, "200": 5000 },
  halfYear: { "25": 6000, "50": 12000, "200": 18000 },
  year: { "25": 12000, "50": 24000, "200": 36000 },
  trial: { "25": 0, "50": 0, "200": 0 }, // Тестовый период бесплатный для всех размеров
};

// Функция для получения _ym_uid из cookie
const getYmUidFromCookies = () => {
  const match = document.cookie.match(/_ym_uid=([^;]+)/);
  return match ? match[1] : null;
};

// Путь к файлу с офертой (файл должен находиться в директории public)
const offerFilePath = '/offer.html';

// Обновлённый массив навигационных ссылок согласно офферу
const navLinks = [
  { id: "intro", label: "Оферта" },
  { id: "terms", label: "Термины" },
  { id: "subject", label: "1. Предмет" },
  { id: "acceptance", label: "2. Акцепт Оферты" },
  { id: "usage", label: "3. Порядок использования Программы" },
  { id: "telegram", label: "4. Telegram-боты" },
  { id: "cost", label: "5. Стоимость и порядок расчетов" },
  { id: "refund", label: "6. Возврат денежных средств" },
  { id: "acceptance-protocol", label: "7. Сдача-приемка" },
  { id: "limitations", label: "8. Ограничения" },
  { id: "responsibility", label: "9. Ответственность" },
  { id: "security", label: "10. Безопасность" },
  { id: "termination", label: "11. Прекращение доступа" },
  { id: "updates", label: "12. Обновления/новые версии" },
  { id: "force-majeure", label: "13. Форс-мажор" },
  { id: "contract-termination", label: "14. Прекращение договоров" },
  { id: "changes", label: "15. Изменение условий Оферты" },
  { id: "contacts", label: "16. Контактные данные" },
];

// Логотип компании из файла favicon.svg
const CompanyLogo = () => (
  <img src="/favicon.svg" alt="Логотип компании" width="32" height="32" />
);

// Тема с фирменными цветами
const themeColors = {
  primary: '#af3831',
  primaryLight: '#c44940',
  primaryDark: '#963029',
  hover: 'rgba(175, 56, 49, 0.04)',
  focus: 'rgba(175, 56, 49, 0.12)'
};

const PaymentForm = () => {
  // Состояния формы
  const [plan, setPlan] = useState("trial");
  const [people, setPeople] = useState(1);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [agree, setAgree] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [queryParams, setQueryParams] = useState({});
  const [offerContent, setOfferContent] = useState("");
  const [isLoadingOffer, setIsLoadingOffer] = useState(false);
  const [userId, setUserId] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [noQuery, setNoQuery] = useState(false);
  const [apiError, setApiError] = useState(false);
  // Добавляем состояние для отслеживания загрузки начального запроса
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  // Для мобильной навигации через Menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const contentRef = useRef(null);

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Валидация email
  const validateEmail = (emailValue) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) return "Email обязателен";
    if (!regex.test(emailValue)) return "Некорректный email";
    return "";
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError(validateEmail(e.target.value));
  };

  // Расчёт тарифа
  const getPackageSize = (numPeople) => {
    if (numPeople <= 25) return "25";
    if (numPeople <= 50) return "50";
    return "200";
  };

  const packageSize = getPackageSize(people);
  // Обновлено: теперь учитываем, что тариф trial имеет нулевую стоимость
  const amount = priceTable[plan]?.[packageSize] || 0;

  // Компонент для отображения сообщения об ошибке
  const ErrorMessage = () => (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        mt: 2, 
        mb: 2, 
        bgcolor: "#FFEBEE", 
        display: "flex", 
        flexDirection: "column",
        alignItems: "center" 
      }}
    >
      <Typography variant="subtitle1" color="error" sx={{ mb: 1 }}>
        Возникла ошибка при подключении к серверу.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Пожалуйста, обратитесь в техническую поддержку:
      </Typography>
      <Button
        variant="outlined"
        color="error"
        href="https://t.me/APOD_lab"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ mt: 1 }}
      >
        Написать в поддержку
      </Button>
    </Paper>
  );

  // Загрузка оферты
  const loadOfferContent = async () => {
    try {
      setIsLoadingOffer(true);
      const response = await fetch(offerFilePath);
      if (!response.ok) {
        throw new Error(`Не удалось загрузить файл оферты: ${response.status}`);
      }
      const htmlContent = await response.text();
      setOfferContent(htmlContent);
    } catch (error) {
      console.error("Ошибка при загрузке оферты:", error);
      setOfferContent(
        '<div class="error">Не удалось загрузить текст оферты. Пожалуйста, обратитесь в поддержку.</div>'
      );
    } finally {
      setIsLoadingOffer(false);
    }
  };

  const handleOpenModal = () => {
    if (!offerContent) loadOfferContent();
    setOpenModal(true);
    if (isMobile) setMenuAnchorEl(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // В десктопе модальное окно закрывается при клике вне его области (backdropClick)
  const handleDialogClose = (event, reason) => {
    handleCloseModal();
  };

  // В мобильной версии FAB с галочкой для принятия оферты
  const handleAcceptOffer = () => {
    setAgree(true);
    setOpenModal(false);
  };

  // Навигация по секциям оферты – теперь для обоих устройств используется смещение 40px
  const handleNavClick = (id) => {
    if (contentRef.current) {
      const target = contentRef.current.querySelector(`#${id}`);
      if (target) {
        const newScrollTop = target.offsetTop - 40;
        contentRef.current.scrollTo({ top: newScrollTop, behavior: "smooth" });
      }
    }
    if (isMobile) setMenuAnchorEl(null);
  };

  // Создание и обновление транзакций
  const createTransaction = async (amount) => {
    try {
      const transactionData = {
        user_id: userId,
        status: "CREATED",
        amount: amount
      };
      const response = await localApi.createTransaction(transactionData);
      console.log("Транзакция создана:", response);
      if (response && response.id) {
        setTransactionId(response.id);
        return response.id;
      } else {
        throw new Error("В ответе нет ID транзакции");
      }
    } catch (err) {
      console.error("Ошибка при создании транзакции:", err);
      throw err;
    }
  };

  const updateTransaction = async (transId, paymentLink, operationId) => {
    try {
      const updateData = {
        id: transId,
        payment_link: paymentLink,
        operation_id: operationId,
        status: "PENDING_PAYMENT"
      };
      const response = await localApi.createTransaction(updateData);
      console.log("Транзакция обновлена:", response);
      return response;
    } catch (err) {
      console.error("Ошибка при обновлении транзакции:", err);
    }
  };

  // Функция обработки бесплатного тестирования
  const handleFreeTrial = async () => {
    if (!userId) {
      alert("Идёт инициализация пользователя (user_id). Подождите немного или обновите страницу.");
      return;
    }
    setIsLoadingPayment(true);
    
    try {
      // Отправляем сообщение о начале бесплатного тестирования
      try {
        await localApi.editMessage(userId, "Пользователь начал бесплатное тестирование");
        console.log("✅ Отправлено сообщение о начале бесплатного тестирования");
      } catch (msgError) {
        console.error("Ошибка при отправке сообщения о начале тестирования:", msgError);
        // Продолжаем процесс даже если отправка сообщения не удалась
      }
      
      // Создаем транзакцию с нулевой суммой
      const transId = await createTransaction(0);
      
      // Сразу помечаем транзакцию как подтвержденную
      try {
        const updateData = {
          id: transId,
          payment_link: "",
          operation_id: "free_trial_" + Date.now(), // Уникальный ID для бесплатного тестирования
          status: "APPROVED"  // Важно: сразу устанавливаем статус APPROVED для бесплатного тестирования
        };
        await localApi.createTransaction(updateData);
        console.log("✅ Создана и подтверждена транзакция для бесплатного тестирования");
      } catch (updateError) {
        console.error("Ошибка при обновлении статуса транзакции:", updateError);
      }
      
      // Перенаправляем на страницу успеха с параметром free_trial=true
      const hardcodedDomain = "https://pay.apod-lab.ru";
      window.location.href = `${hardcodedDomain}/form_success?user_id=${userId}&transaction_id=${transId}&free_trial=true`;
      
    } catch (err) {
      console.error("Ошибка при оформлении бесплатного тестирования:", err);
      alert("Ошибка при оформлении бесплатного тестирования, см. консоль.");
      setIsLoadingPayment(false);
    }
  };

  // Модифицированная функция handlePayment в PaymentForm.js
  const handlePayment = async () => {
    if (!userId) {
      alert("Идёт инициализация пользователя (user_id). Подождите немного или обновите страницу.");
      return;
    }
    setIsLoadingPayment(true);
    
    try {
      // Добавляем: отправляем сообщение о том, что пользователь перешел к оплате
      try {
        await localApi.editMessage(userId, "Пользователь перешел к оплате");
        console.log("✅ Отправлено сообщение о переходе к оплате");
      } catch (msgError) {
        console.error("Ошибка при отправке сообщения о переходе к оплате:", msgError);
        // Продолжаем процесс оплаты даже если отправка сообщения не удалась
      }
      
      const transId = await createTransaction(amount);
      
      // Вместо использования переменной окружения, жестко пропишем домен
      // Это позволит исключить проблемы с доступом к переменным окружения
      const hardcodedDomain = "https://pay.apod-lab.ru";
      
      // Создаем полные URL прямо здесь
      const successUrl = `${hardcodedDomain}/form_success?user_id=${userId}&transaction_id=${transId}`;
      const failUrl = `${hardcodedDomain}/form_fail?user_id=${userId}&transaction_id=${transId}`;
      
      console.log("Домен из .env:", process.env.REACT_APP_SITE_DOMAIN);
      console.log("Жестко заданный домен:", hardcodedDomain);
      console.log("Финальные URL для отправки:");
      console.log("- successUrl:", successUrl);
      console.log("- failUrl:", failUrl);
      
      let response;
      if (plan === "trial" || plan === "month") {
        // Напрямую вызываем fetchData для отладки
        const requestBody = {
          amount,
          email,
          redirect_url: successUrl,
          fail_redirect_url: failUrl,
          user_id: userId
        };
        
        console.log("Тело запроса для createPaymentReceipt:", requestBody);
        
        // Используем API как обычно, но с отладочной информацией
        response = await localApi.createPaymentReceipt(amount, email, successUrl, failUrl, userId);
      } else {
        const months = plan === "halfYear" ? 6 : 12;
        const monthlyAmount = amount / months;
        response = await localApi.createSubscriptionReceipt(months, monthlyAmount, { email }, [], successUrl, failUrl, userId);
      }
      
      console.log("Ответ createPayment / createSubscription:", response);
      if (response && response.url) {
        await updateTransaction(transId, response.url, response.operationId || "");
        window.location.href = response.url;
      } else {
        alert("Не удалось получить ссылку для оплаты (url отсутствует).");
        setIsLoadingPayment(false);
      }
    } catch (err) {
      console.error("Ошибка при создании платежа:", err);
      alert("Ошибка при создании платежа, см. консоль.");
      setIsLoadingPayment(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const parsed = {};
    params.forEach((value, key) => {
      parsed[key] = value;
    });
    setQueryParams(parsed);
    if (!Object.keys(parsed).length) {
      setNoQuery(true);
      return;
    }
    if (parsed.companyCountPeople) {
      setPeople(parseInt(parsed.companyCountPeople) || 1);
    }
    if (parsed.emailUser) {
      setEmail(parsed.emailUser);
      setEmailError(validateEmail(parsed.emailUser));
    }
    
    const doUpsertCompany = (reason) => {
      console.log(`→ upsertCompany, причина: ${reason}`);
      // Устанавливаем состояние загрузки
      setIsLoadingInitial(true);
      
      localApi
        .upsertCompany({
          userName: parsed.userName || "",
          companyName: parsed.companyName || "",
          companyCountPeople: parsed.companyCountPeople || "",
          telegramName: parsed.telegramName || "",
          phoneUser: parsed.phoneUser || "",
          emailUser: parsed.emailUser || "",
          ya_uid: currentYmUid || "" // Добавляем передачу ym_uid
        })
        .then((res) => {
          console.log("✅ Результат upsertCompany:", res);
          if (res && res.user_id) {
            setUserId(res.user_id);
            // Сбрасываем состояние ошибки, если запрос был успешным
            setApiError(false);
            
            // Добавлено: получаем полную информацию о пользователе после upsertCompany
            return localApi.searchUsers({ id: res.user_id });
          } else {
            console.warn("В ответе upsertCompany нет user_id!");
            // Устанавливаем состояние ошибки, если в ответе нет user_id
            setApiError(true);
            // Завершаем загрузку
            setIsLoadingInitial(false);
            return null;
          }
        })
        .then((userData) => {
          if (userData && Array.isArray(userData) && userData.length > 0) {
            console.log("✅ Получена обновленная информация о пользователе:", userData[0]);
            // Здесь можно сохранить дополнительные данные в state если нужно
          }
          // Завершаем загрузку после получения данных
          setIsLoadingInitial(false);
        })
        .catch((err) => {
          console.error("Ошибка upsertCompany или получения данных пользователя:", err);
          // Устанавливаем состояние ошибки при любом исключении
          setApiError(true);
          // Завершаем загрузку в случае ошибки
          setIsLoadingInitial(false);
        });
    };

    const currentYmUid = getYmUidFromCookies();
    if (!currentYmUid) {
      console.log("Нет _ym_uid => upsertCompany (анонимный визит)");
      doUpsertCompany("no _ym_uid in cookie");
      return;
    }
    const storedUid = localStorage.getItem("ym_uid");
    if (!storedUid) {
      console.log("Первый визит => записываем ym_uid и upsertCompany");
      localStorage.setItem("ym_uid", currentYmUid);
      doUpsertCompany("first visit");
    } else {
      if (storedUid === currentYmUid) {
        console.log("Возвращающийся пользователь => upsertCompany (backend сам разберется)");
        // Убираем doSearchUsers - пусть backend сам найдет пользователя через upsertCompany
        doUpsertCompany("returning user");
      } else {
        console.log("UID changed => upsertCompany");
        localStorage.setItem("ym_uid", currentYmUid);
        doUpsertCompany("UID changed");
      }
    }
  }, []);

  if (noQuery) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 400, padding: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h5" align="center" sx={{ mb: 2 }} gutterBottom>
              Упс!
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 2 }}>
              Кажется, вы попали сюда по ошибке. Если нет, обратитесь в поддержку.
            </Typography>
            <Button
              variant="contained"
              href="https://t.me/APOD_lab"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                mt: 1,
                bgcolor: themeColors.primary,
                '&:hover': { 
                  bgcolor: themeColors.primaryLight 
                },
                '&:active': {
                  bgcolor: themeColors.primaryDark
                }
              }}
              fullWidth
            >
              Связаться с поддержкой
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 400, padding: 3, boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 4 }}>
            <CompanyLogo />
            <Typography variant="h5" align="center" sx={{ ml: 2 }} gutterBottom>
              Оплата тарифа
            </Typography>
          </Box>
          
          {/* Условное отображение - при ошибке показываем только ErrorMessage */}
          {apiError ? (
            <ErrorMessage />
          ) : (
            <>
              {/* Форма оплаты скрывается при ошибке */}
              <FormControl fullWidth variant="outlined">
                <InputLabel id="tariff-label" shrink sx={{ color: themeColors.primary }}>
                  Выберите тариф
                </InputLabel>
                <Select
                  labelId="tariff-label"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  label="Выберите тариф"
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: themeColors.primary,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: themeColors.primaryLight,
                    },
                    '.MuiSvgIcon-root': {
                      color: themeColors.primary
                    }
                  }}
                >
                  <MenuItem value="trial">Тестировать (14 дней)</MenuItem>
                  <MenuItem value="month">1 месяц</MenuItem>
                  <MenuItem value="halfYear">6 месяцев</MenuItem>
                  <MenuItem value="year">1 год</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Количество человек"
                type="number"
                fullWidth
                margin="normal"
                sx={{ 
                  mt: 2,
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: themeColors.primary,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.primary,
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.primaryLight,
                  },
                }}
                value={people}
                onChange={(e) => setPeople(e.target.value)}
                onBlur={() => setPeople(Number.isNaN(parseInt(people)) ? 1 : parseInt(people))}
              />
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, mb: 2 }}>
                <TextField
                  label="Ваш email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={handleEmailChange}
                  error={!!emailError}
                  helperText={emailError}
                  sx={{ 
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: !!emailError ? 'error.main' : themeColors.primary,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: !!emailError ? 'error.main' : themeColors.primary,
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: !!emailError ? 'error.light' : themeColors.primaryLight,
                    },
                  }}
                />
                <Tooltip title="Мы берём ваш email, чтобы отправить чек" placement={isMobile ? "bottom" : "right"} arrow>
                  <InfoOutlinedIcon sx={{ cursor: "pointer", fontSize: "1.8rem", color: themeColors.primary }} />
                </Tooltip>
              </Box>
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Checkbox 
                    checked={agree} 
                    onChange={() => setAgree(!agree)} 
                    sx={{
                      color: themeColors.primary,
                      '&.Mui-checked': {
                        color: themeColors.primary,
                      }
                    }}
                  />
                }
                label={
                  <span>
                    Я соглашаюсь с{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenModal();
                      }}
                      style={{ textDecoration: "underline", cursor: "pointer", color: themeColors.primary }}
                    >
                      офертой
                    </a>
                  </span>
                }
              />
              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                <Button
                  variant="contained"
                  disabled={!agree || !email || !!emailError || isLoadingPayment || isLoadingInitial}
                  onClick={plan === "trial" ? handleFreeTrial : handlePayment}
                  sx={{ 
                    bgcolor: themeColors.primary, 
                    '&:hover': { 
                      bgcolor: themeColors.primaryLight 
                    },
                    '&:active': {
                      bgcolor: themeColors.primaryDark
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(175, 56, 49, 0.3)'
                    }
                  }}
                >
                  {isLoadingInitial ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Выполняю работу...
                    </>
                  ) : isLoadingPayment ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      {plan === "trial" ? "Активация тестирования..." : "Генерирую ссылку для оплаты..."}
                    </>
                  ) : (
                    <>
                    {plan === "trial" ? "Начать бесплатное тестирование" : `Оплатить ${amount.toLocaleString("ru-RU")} ₽`}
                    </>
                  )}
                </Button>
                {plan === "trial" && (
                  <Tooltip title="Тестовый период предоставляется бесплатно на 14 дней." placement="top" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: "1.8rem", cursor: "pointer", color: themeColors.primary }} />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2, display: "none" }}>
                user_id: {userId || "—"}, transaction_id: {transactionId || "—"}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={openModal}
        // Убираем TransitionComponent, чтобы использовать стандартный Fade
        onClose={handleDialogClose}
        fullScreen={isMobile}
        fullWidth={!isMobile}
        maxWidth={!isMobile ? "md" : undefined}
      >
        <DialogContent sx={{ display: "flex", p: 0, height: "calc(100vh - 64px)" }}>
          {/* В десктопной версии навигация показывается как боковой блок */}
          {!isMobile && (
            <Box sx={{ width: 280, borderRight: "1px solid #ddd", overflowY: "auto", p: 2 }}>
              <List>
                {navLinks.map((link) => (
                  <ListItem button key={link.id} onClick={() => handleNavClick(link.id)}>
                    <ListItemText primary={link.label} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          <Box sx={{ flex: 1, overflowY: "auto", position: "relative", p: 2 }} ref={contentRef}>
            {isLoadingOffer ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>Загрузка оферты...</Typography>
              </Box>
            ) : (
              <div id="offer-dialog-description" dangerouslySetInnerHTML={{ __html: offerContent }} />
            )}
          </Box>
        </DialogContent>
        {/* В мобильной версии: FAB-бургер для навигационного меню через Menu */}
        {isMobile && (
          <>
            <Fab
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              sx={{ 
                position: "fixed", 
                bottom: 90, 
                right: 20, 
                zIndex: 1300,
                bgcolor: themeColors.primary,
                '&:hover': { 
                  bgcolor: themeColors.primaryLight 
                }
              }}
            >
              <MenuIcon />
            </Fab>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { mt: -8 } }} // Меняем отступ сверху
            >
              {navLinks.map((link) => (
                <MenuItem key={link.id} onClick={() => handleNavClick(link.id)}>
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {/* В мобильной версии: FAB с галочкой для принятия оферты */}
        {isMobile && (
          <Fab
            aria-label="accept"
            onClick={handleAcceptOffer}
            sx={{ 
              position: "fixed", 
              bottom: 20, 
              right: 20,
              bgcolor: themeColors.primary,
              '&:hover': { 
                bgcolor: themeColors.primaryLight 
              }
            }}
          >
            <CheckIcon />
          </Fab>
        )}
      </Dialog>
    </Container>
  );
};

export default PaymentForm;